import { useEffect, type RefObject } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Traps Tab/Shift+Tab inside `ref` while `active`, moves initial focus into
 * the container and restores focus to the previously focused element on
 * deactivation. Calls `onEscape` for the Escape key.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  opts: { onEscape?: () => void; initialFocus?: RefObject<HTMLElement | null>; restoreFocus?: boolean } = {},
) {
  const { onEscape, initialFocus, restoreFocus = true } = opts
  useEffect(() => {
    if (!active) return
    const container = ref.current
    if (!container) return
    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement)

    // Initial focus — next frame so the element is painted/animated in.
    const raf = requestAnimationFrame(() => {
      const target = initialFocus?.current ?? focusables()[0] ?? container
      target.focus({ preventScroll: true })
    })

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onEscape) { e.stopPropagation(); onEscape() }
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) { e.preventDefault(); container.focus(); return }
      const first = items[0]
      const last = items[items.length - 1]
      const current = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) { e.preventDefault(); last.focus() }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault(); first.focus()
      }
    }
    container.addEventListener('keydown', onKey)
    return () => {
      cancelAnimationFrame(raf)
      container.removeEventListener('keydown', onKey)
      if (restoreFocus && previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true })
      }
    }
  }, [ref, active, onEscape, initialFocus, restoreFocus])
}

/** Locks body scroll while `active`. */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [active])
}
